import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Icon } from '../../components/icons/Icon';
import { cn, getTestId, SEVERITY_CONFIG, type AlertSeverity } from '../../utils';
import type { IconName } from '../../icons/names';

const alertBannerVariants = cva('border rounded-lg py-sm flex items-start gap-sm', {
  variants: {
    size: {
      xs: 'px-sm',
      sm: 'px-md'
    }
  },
  defaultVariants: {
    size: 'xs'
  }
});

const alertBannerTextVariants = cva('', {
  variants: {
    size: {
      xs: 'text-xs',
      sm: 'text-sm'
    }
  },
  defaultVariants: {
    size: 'xs'
  }
});

/** Title colour: `warn-text` / `info-text` fall below 4.5:1 on their background, so they stay neutral. */
const TITLE_COLOR: Record<AlertSeverity, string> = {
  error: 'text-error-text',
  warning: 'text-foreground-base',
  info: 'text-foreground-base',
  success: 'text-success-text'
};

/** Body colour per size; the severity shows on icon, border and background. */
const BODY_COLOR = {
  xs: 'text-foreground-muted',
  sm: 'text-foreground-base'
} as const;

/** True when a node renders something: `0` counts, `false` / `null` / `''` do not. */
const hasContent = (node: React.ReactNode) =>
  React.Children.toArray(node).some(child => child !== '');

export interface AlertBannerProps extends VariantProps<typeof alertBannerVariants> {
  /** Visual variant — drives color scheme and default icon */
  variant: AlertSeverity;
  /** Live-region role, none by default (static content is not announced). `alert` for an error
   * that appears after an action, `status` for a polite update after one. */
  role?: 'alert' | 'status';
  /** Optional title line. Accepts a plain string or a ReactNode (e.g. an
   * `ErrorEntry` element) so validation issues can be rendered with their
   * `meta.expected` / `meta.actual` context. */
  title?: React.ReactNode;
  /** Optional description below the title — plain text or inline elements. */
  body?: React.ReactNode;
  /** Body content below the title; may hold block elements such as lists. */
  children?: React.ReactNode;
  /** Control on the right, e.g. a dismiss button; centred on the first line. */
  action?: React.ReactNode;
  /** Override the default icon */
  icon?: IconName;
  className?: string;
  /** Test ID for e2e / Storybook interaction tests */
  testId?: string;
  /** Element id (also used as testId fallback) */
  id?: string;
}

/**
 * Inline alert banner for warnings, errors, info and success messages.
 * `size='xs'` (default) is the compact form-area style; `size='sm'` is used by `Modal.Warning` & co.
 */
export const AlertBanner = React.forwardRef<HTMLDivElement, AlertBannerProps>(
  ({ variant, role, title, body, children, action, icon, size, className, testId, id }, ref) => {
    const s = SEVERITY_CONFIG[variant];
    const textSize = alertBannerTextVariants({ size });
    const hasTitle = hasContent(title);
    const hasBody = hasContent(body);
    const hasChildren = hasContent(children);
    const hasAction = hasContent(action);
    const bodyColor = BODY_COLOR[size ?? 'xs'];
    return (
      <div
        ref={ref}
        role={role}
        className={cn(s.bgColor, s.borderColor, alertBannerVariants({ size }), className)}
        data-testid={getTestId({ testId, id })}
        id={id}
      >
        <Icon name={icon ?? s.icon} className={cn('w-4 h-4', s.iconColor, 'mt-2xs shrink-0')} />
        <div className={cn('min-w-0 break-words', hasAction && 'flex-1')}>
          {hasTitle && <p className={cn(textSize, 'font-medium', TITLE_COLOR[variant])}>{title}</p>}
          {hasBody && <p className={cn(textSize, hasTitle && 'mt-2xs', bodyColor)}>{body}</p>}
          {hasChildren && (
            <div className={cn(textSize, (hasTitle || hasBody) && 'mt-2xs', bodyColor)}>
              {children}
            </div>
          )}
        </div>
        {hasAction && (
          // The negative margin keeps a button from growing the banner past one text line.
          <div className='shrink-0 -my-sm'>{action}</div>
        )}
      </div>
    );
  }
);

AlertBanner.displayName = 'AlertBanner';
